/**
 * TeamContext — provides the user's active team + list of teams they belong to.
 *
 * Active team is the team whose context the user is currently working in.
 * It's persisted to localStorage so it survives page reloads, and read by
 * the axios interceptor in `utils/api.js` to attach the `X-Team-Id` header
 * to every API request automatically.
 *
 * Backend reads `X-Team-Id` via the `currentTeam` middleware
 * (backend/src/middleware/currentTeam.js) which scopes queries on the
 * relevant routes (currently `GET /api/webinars/my`).
 *
 * Three-state UI semantics:
 *   - 0 teams → no switcher visible in nav
 *   - 1 team  → switcher renders as a static label (no dropdown)
 *   - 2+ teams → dropdown switcher
 *
 * On logout (AuthContext clears tokens), the localStorage key is also
 * cleared so the next login starts fresh.
 */

import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import api from '../utils/api';
import { AuthContext } from './AuthContext';

const STORAGE_KEY = 'canira_current_team_id';

export const TeamContext = createContext({
  currentTeam: null,
  availableTeams: [],
  setCurrentTeam: () => {},
  refreshTeams: () => {},
  loading: false,
});

export const TeamProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext) || {};
  const [availableTeams, setAvailableTeams] = useState([]);
  const [currentTeamId, setCurrentTeamIdState] = useState(() => {
    // Hydrate from localStorage on mount so the active team survives reload.
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  // Track the last user we fetched for so we don't refetch on every render.
  const lastFetchedUserRef = useRef(null);

  const fetchTeams = useCallback(async () => {
    if (!currentUser?.id) {
      setAvailableTeams([]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/teams');
      const teams = response?.data?.teams || [];
      setAvailableTeams(teams);

      // If the currently-selected team is no longer in the list (user
      // was removed, or team was deleted), clear the selection. Don't
      // auto-pick a new one — let the user pick explicitly.
      if (currentTeamId && !teams.some((t) => t.id === currentTeamId)) {
        setCurrentTeamIdState(null);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch { /* ignore */ }
      }
    } catch (e) {
      // Silent fail — user may not have permission, network may be down,
      // or the endpoint may not be available. Treat as "no teams" so
      // the rest of the UI keeps working.
      setAvailableTeams([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, currentTeamId]);

  // Fetch teams whenever the logged-in user changes (login, logout, switch).
  useEffect(() => {
    if (currentUser?.id !== lastFetchedUserRef.current) {
      lastFetchedUserRef.current = currentUser?.id || null;
      if (currentUser?.id) {
        fetchTeams();
      } else {
        // Logged out — clear everything.
        setAvailableTeams([]);
        setCurrentTeamIdState(null);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch { /* ignore */ }
      }
    }
  }, [currentUser?.id, fetchTeams]);

  const setCurrentTeam = useCallback((teamId) => {
    // Pass null/undefined/empty string to clear the active team.
    if (!teamId) {
      setCurrentTeamIdState(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch { /* ignore */ }
      return;
    }
    // Verify the team is one the user belongs to before persisting —
    // prevents stale state if a teamId is passed in from somewhere
    // unexpected.
    const isMember = availableTeams.some((t) => t.id === teamId);
    if (!isMember) {
      // Refresh and try again — the user may have just been added.
      fetchTeams();
      return;
    }
    setCurrentTeamIdState(teamId);
    try {
      localStorage.setItem(STORAGE_KEY, teamId);
    } catch { /* ignore */ }
  }, [availableTeams, fetchTeams]);

  // Resolve the full team object for the active id (nav, scoping logic
  // both want the name/logo, not just the id).
  const currentTeam = currentTeamId
    ? availableTeams.find((t) => t.id === currentTeamId) || null
    : null;

  const value = {
    currentTeam,
    availableTeams,
    setCurrentTeam,
    refreshTeams: fetchTeams,
    loading,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export const useTeam = () => {
  const ctx = useContext(TeamContext);
  if (!ctx) {
    throw new Error('useTeam must be used inside a <TeamProvider>');
  }
  return ctx;
};
